import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { api } from "../api/client";
import type { Branding, Project } from "../api/types";

interface ProjectCtx {
  projects: Project[];
  active: Project | null;
  loading: boolean;
  setActiveId: (id: string) => void;
  refresh: () => Promise<void>;
  createProject: (name: string) => Promise<Project>;
  updateProject: (id: string, patch: { name?: string; branding?: Branding }) => Promise<Project>;
}

const Ctx = createContext<ProjectCtx>(null as unknown as ProjectCtx);
const LS_ACTIVE = "gc_active_project";

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeId, setActiveIdState] = useState<string | null>(
    () => localStorage.getItem(LS_ACTIVE),
  );
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const list = await api.listProjects();
    setProjects(list);
    setActiveIdState((cur) => {
      if (cur && list.some((p) => p.id === cur)) return cur;
      return list[0]?.id ?? null;
    });
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const setActiveId = useCallback((id: string) => {
    setActiveIdState(id);
    localStorage.setItem(LS_ACTIVE, id);
  }, []);

  const createProject = useCallback(
    async (name: string) => {
      const p = await api.createProject(name);
      await refresh();
      setActiveId(p.id);
      return p;
    },
    [refresh, setActiveId],
  );

  const updateProject = useCallback(
    async (id: string, patch: { name?: string; branding?: Branding }) => {
      const p = await api.updateProject(id, patch);
      setProjects((cur) => cur.map((x) => (x.id === id ? p : x)));
      return p;
    },
    [],
  );

  const active = projects.find((p) => p.id === activeId) ?? null;

  return (
    <Ctx.Provider value={{ projects, active, loading, setActiveId, refresh, createProject, updateProject }}>
      {children}
    </Ctx.Provider>
  );
}

export const useProjects = () => useContext(Ctx);
